/**
 * Tools Service
 * Handles flashcards, decks, and notes management
 */

import {
  FlashcardDecksRecord,
  FlashcardsRecord,
  NotesRecord,
} from '@/types/collections';
import { createRepository } from '@/services/repository';

class ToolsService {
  private decksRepo = createRepository<FlashcardDecksRecord>('flashcard_decks');
  private cardsRepo = createRepository<FlashcardsRecord>('flashcards');
  private notesRepo = createRepository<NotesRecord>('notes');

  setUserId(userId: string): void {
    this.decksRepo.setUserId(userId);
    this.cardsRepo.setUserId(userId);
    this.notesRepo.setUserId(userId);
  }

  // Flashcard Decks API
  async getDecks() {
    const result = await this.decksRepo.list({
      sort: '-updated',
    });
    return result.records;
  }

  async createDeck(data: Record<string, unknown>) {
    return this.decksRepo.create(data as Partial<FlashcardDecksRecord>);
  }

  async updateDeck(id: string, data: Partial<FlashcardDecksRecord>) {
    return this.decksRepo.update(id, data);
  }

  async deleteDeck(id: string) {
    return this.decksRepo.delete(id);
  }

  // Flashcards API
  async getCards(deckId: string) {
    const result = await this.cardsRepo.list({
      filter: `deck = "${deckId}"`,
      sort: '-updated',
    });
    return result.records;
  }

  async createCard(data: Record<string, unknown>) {
    return this.cardsRepo.create(data as Partial<FlashcardsRecord>);
  }

  async updateCard(id: string, data: Partial<FlashcardsRecord>) {
    return this.cardsRepo.update(id, data);
  }

  async deleteCard(id: string) {
    return this.cardsRepo.delete(id);
  }

  // Notes API (from tools module)
  async getNotes() {
    const result = await this.notesRepo.list({
      sort: '-updated',
    });
    return result.records;
  }

  async createNote(data: Record<string, unknown>) {
    return this.notesRepo.create(data as Partial<NotesRecord>);
  }

  async updateNote(id: string, data: Partial<NotesRecord>) {
    return this.notesRepo.update(id, data);
  }

  async deleteNote(id: string) {
    return this.notesRepo.delete(id);
  }
}

export const toolsService = new ToolsService();

// Type aliases for backwards compatibility
export const flashcardsService = toolsService;
export const writerService = toolsService;
export const sheetsService = toolsService;

